import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { sharePdfBlob } from "@/lib/downloadPdf";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Clock, Share2, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SwipeBack from "@/components/SwipeBack";

async function downloadInvoice(bookingId) {
  // Un seul bouton : ouvre directement la feuille de partage native
  // (iMessage, Mail, WhatsApp, Enregistrer dans Fichiers…). Le chauffeur
  // peut visualiser ET envoyer sa facture en un tap.
  try {
    const res = await api.get(`/driver/bookings/${bookingId}/invoice.pdf`, { responseType: "blob" });
    await sharePdfBlob(res.data, `saor-facture-${bookingId.slice(0, 8)}.pdf`, {
      title: "Facture Saor",
      text: "Facture de course Saor.",
    });
  } catch (e) {
    const msg = String(e?.message || "");
    if (!msg.toLowerCase().includes("abort") && !msg.toLowerCase().includes("cancel")) {
      toast.error(e?.response?.data?.detail || e.message || "Impossible d'ouvrir la facture");
    }
  }
}

const STATUS_LABEL = {
  pending: "En attente",
  accepted: "Acceptée",
  picked_up: "Client pris en charge",
  in_progress: "Trajet en cours",
  completed: "Terminée",
  cancelled: "Annulée",
};

function formatLocalDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
}

/** Read-only ride history for the driver. Actions (pickup, GPS, chat, etc.)
 *  live on /dashboard/driver/active. This page is the "archive".
 *
 *  Retour :
 *    • Bouton flèche en haut à gauche (fallback fiable).
 *    • Geste "swipe from left edge → right" (iOS-style) via <SwipeBack>. */
export default function DriverHistory() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/driver/bookings/mine");
        setBookings(data.bookings || []);
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q ? bookings : bookings.filter((b) =>
    [b.client_name, b.arrival_address, b.departure_address, b.hotel_name]
      .filter(Boolean).some((s) => s.toLowerCase().includes(q))
  );

  const stats = {
    total: bookings.length,
    completed: bookings.filter((b) => b.status === "completed").length,
    revenue: bookings
      .filter((b) => b.status === "completed")
      .reduce((acc, b) => acc + (b.driver_revenue || 0), 0),
  };

  return (
    <SwipeBack fallback="/dashboard/driver">
      <div className="mx-auto max-w-4xl px-5 pb-10 safe-top sm:px-8 sm:pb-16" data-testid="driver-history">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard/driver", { replace: true }))}
          className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1.5 text-sm font-medium text-foreground shadow-sm backdrop-blur transition hover:bg-white"
          data-testid="driver-history-back-btn"
          aria-label="Retour"
        >
          <ChevronLeft className="h-4 w-4" /> Retour
        </button>
        <p className="text-xs uppercase tracking-widest text-primary">Historique</p>
      <h1 className="mt-2 font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">
        Mes courses — {profile?.first_name || "chauffeur"}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Consultez toutes vos courses passées et en cours. Pour la course en direct, allez sur
        « Course active ».
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-3">
        <Stat label="Total courses" value={stats.total} />
        <Stat label="Terminées" value={stats.completed} />
        <Stat label="Revenus cumulés" value={`${stats.revenue.toFixed(2)} €`} />
      </div>

      <div className="relative mt-8">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher par client, adresse, hôtel…"
          className="pl-9"
          data-testid="history-search"
        />
      </div>

      <div className="mt-8 space-y-3 min-h-[120px]">
        {loading ? null : filtered.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
            {bookings.length === 0 ? "Aucune course pour le moment." : "Aucune course ne correspond à cette recherche."}
          </div>
        ) : filtered.map((b) => {
          const statusColor = b.status === "completed" ? "bg-[hsl(var(--success))] text-white"
            : b.status === "cancelled" ? "bg-destructive text-destructive-foreground"
            : "bg-secondary text-foreground";
          return (
            <div key={b.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`history-${b.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={statusColor}>{STATUS_LABEL[b.status]}</Badge>
                {(b.booking_type || "immediate") === "scheduled" && b.scheduled_at && (
                  <Badge className="gap-1 rounded-full bg-accent text-accent-foreground">
                    <Clock className="h-3 w-3" /> {formatLocalDateTime(b.scheduled_at)}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">{formatLocalDateTime(b.created_at)}</span>
              </div>
              <p className="mt-2 font-medium">{b.client_name}{b.hotel_name ? ` · ${b.hotel_name}` : ""}</p>
              <p className="text-sm text-muted-foreground">
                <MapPin className="mr-1.5 inline h-3.5 w-3.5" /> {b.departure_address}
              </p>
              <p className="text-sm text-muted-foreground">→ {b.arrival_address}</p>
              <div className="mt-2 inline-flex flex-wrap items-center gap-2 text-xs">
                {b.price != null && (
                  <span className="rounded-md border border-border bg-secondary/40 px-2.5 py-1">
                    Prix course : <strong>{b.price} €</strong>
                  </span>
                )}
                {b.driver_revenue != null && (
                  <span className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-2.5 py-1 text-[hsl(var(--success))]">
                    Votre revenu : <strong>{b.driver_revenue} €</strong>
                  </span>
                )}
                {b.distance_km != null && (
                  <span className="text-muted-foreground">{b.distance_km.toFixed(1)} km</span>
                )}
                {b.payment_status === "paid" && (
                  <span className="text-[hsl(var(--success))]">✓ Payée</span>
                )}
              </div>
              {b.payment_status === "paid" && (
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadInvoice(b.id)}
                    className="gap-1.5 rounded-full border-primary/40 text-primary hover:bg-primary/5"
                    data-testid={`download-invoice-${b.id}`}
                  >
                    <Share2 className="h-3.5 w-3.5" /> Facture
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </SwipeBack>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 font-[Outfit] text-2xl font-semibold text-primary">{value}</p>
    </div>
  );
}
